import type { TOCItem } from "@/types/modules";

export type SlideImage = { url: string; slideNumber: number };

export interface ViewerSlideDetails {
  slideId: string;
  title: string;
  chapter: string | null;
  slideNumber: number | null;
  paletteSlot: number;
  details: string | null;
}

export interface ViewerTitleCard {
  title: string;
  presenter: string | null;
  role: string | null;
}

export interface ViewerDetailsIndex {
  bySlide: Map<string, ViewerSlideDetails>;
  slideTotal: number;
  titleCard: ViewerTitleCard;
  toc: Record<string, TOCItem> | null;
  slideIdByNumber: Map<number, string>;
}

const PALETTE_SLOTS = 5;

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function slideNumberOf(page: any): number | null {
  return numberOrNull(page?.sourceSlideNumber) ?? numberOrNull(page?.slideNumber);
}

function tocOfPages(pages: any[]): Record<string, TOCItem> | null {
  for (const page of pages) {
    for (const module of page?.modules ?? []) {
      if (module?.type === "toc" && module?.data && typeof module.data === "object") {
        return module.data as Record<string, TOCItem>;
      }
    }
  }
  return null;
}

function personName(author: any): string | null {
  const first = stringOrNull(author?.first_name);
  const last = stringOrNull(author?.last_name);
  const full = [first, last].filter(Boolean).join(" ");
  return full || null;
}

function titleCardOf(pages: any[], fallback?: Partial<ViewerTitleCard>): ViewerTitleCard {
  const intro = pages
    .flatMap((page) => page?.modules ?? [])
    .find((module: any) => module?.type === "intro");
  return {
    title:
      stringOrNull(intro?.title) ?? stringOrNull(fallback?.title) ?? "",
    presenter: personName(intro?.author) ?? fallback?.presenter ?? null,
    role: stringOrNull(intro?.author?.role) ?? fallback?.role ?? null,
  };
}

export function buildViewerDetailsIndex(
  pages: any[],
  fallback?: Partial<ViewerTitleCard>,
  toc?: Record<string, TOCItem> | null,
): ViewerDetailsIndex {
  const bySlide = new Map<string, ViewerSlideDetails>();
  const slideIdByNumber = new Map<number, string>();
  let slideTotal = 0;
  pages.forEach((page, index) => {
    const slideId = typeof page?.slideId === "string" ? page.slideId : "";
    if (!slideId || bySlide.has(slideId)) return;
    const slideNumber = slideNumberOf(page);
    if (slideNumber != null) {
      slideTotal = Math.max(slideTotal, slideNumber);
      if (!slideIdByNumber.has(slideNumber)) slideIdByNumber.set(slideNumber, slideId);
    }
    const ordinal = slideNumber ?? index;
    bySlide.set(slideId, {
      slideId,
      title: stringOrNull(page?.title) ?? "",
      chapter: stringOrNull(page?.chapter),
      slideNumber,
      paletteSlot: (Math.abs(Math.trunc(ordinal)) % PALETTE_SLOTS) + 1,
      details: stringOrNull(page?.details),
    });
  });
  const tocSource = toc && Object.keys(toc).length > 0 ? toc : tocOfPages(pages);
  return {
    bySlide,
    slideTotal,
    titleCard: titleCardOf(pages, fallback),
    toc: tocSource && Object.keys(tocSource).length > 0 ? tocSource : null,
    slideIdByNumber,
  };
}

export function slideImagesFromThumbnails(
  slides: {
    slideId: string;
    sourceSlideNumber: number;
    thumbnailUrl: string | null;
  }[],
): Record<string, SlideImage> {
  const images: Record<string, SlideImage> = {};
  for (const slide of slides) {
    if (slide.thumbnailUrl) {
      images[slide.slideId] = {
        url: slide.thumbnailUrl,
        slideNumber: slide.sourceSlideNumber,
      };
    }
  }
  return images;
}

export function slideImagesFromSignedUrls(
  slides: { slideId: string; sourceSlideNumber?: number | null }[],
  signedUrls: (string | null | undefined)[] | null | undefined,
): Record<string, SlideImage> {
  const images: Record<string, SlideImage> = {};
  if (!signedUrls) return images;
  for (const slide of slides) {
    const number = slide.sourceSlideNumber;
    if (typeof number !== "number" || number < 1) continue;
    const url = signedUrls[number - 1];
    if (url) images[slide.slideId] = { url, slideNumber: number };
  }
  return images;
}
